import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { useAuth } from './AuthContext';
import Mark from './components/Mark';
import LanguageSelector from './components/LanguageSelector';
import { PROTOTYPES } from './prototypes';
import { useI18n } from './i18n';
import { colors, font, type, layout, elevation, gradient } from './theme/tokens';
import { Bubble } from './components/BubbleButton';

const Eyebrow: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color }) => (
  <Typography sx={{ ...type.eyebrow, color: color ?? colors.inkFaint }}>
    {children}
  </Typography>
);

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useI18n();

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: colors.bg,
      fontFamily: font.sans,
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* Topbar */}
      <Box sx={{
        height: layout.topbarHeight,
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: colors.barTint,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
      }}>
        <Box sx={{
          width: '100%',
          maxWidth: layout.maxWidth,
          mx: 'auto',
          px: layout.gutter,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Mark />
            <Box sx={{ width: '1px', height: 18, backgroundColor: colors.borderStrong }} />
            <Typography sx={{
              fontFamily: font.sans,
              fontSize: '13px',
              color: colors.inkMuted,
              display: { xs: 'none', sm: 'block' },
            }}>
              {t('landing.title')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LanguageSelector />
            <Typography sx={{
              ...type.meta,
              color: colors.inkFaint,
              display: { xs: 'none', md: 'block' },
            }}>
              {user?.email}
            </Typography>
            <Button
              onClick={signOut}
              disableRipple
              sx={{
                fontFamily: font.sans,
                fontSize: '13px',
                fontWeight: 500,
                color: colors.inkMuted,
                textTransform: 'none',
                px: 1.75,
                py: 0.75,
                minWidth: 0,
                backgroundColor: colors.bg,
                border: `1px solid ${colors.borderStrong}`,
                borderRadius: layout.radius,
                '&:hover': {
                  color: colors.blue,
                  borderColor: colors.blue,
                  backgroundColor: colors.bg,
                },
              }}
            >
              {t('app.signOut')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Hero — styleguide 'color morph': blue bands over the base navy, at 32° */}
      <Box sx={{
        width: '100%',
        background: gradient.colorMorph,
      }}>
        <Box sx={{
          maxWidth: layout.maxWidth,
          mx: 'auto',
          width: '100%',
          px: layout.gutter,
          pt: { xs: 7, md: 12 },
          pb: { xs: 6, md: 9 },
        }}>
        <Box sx={{
          width: 56, height: 5, mb: 3,
          backgroundColor: colors.accent,
          borderRadius: layout.radiusPill,
        }} />

        <Eyebrow color={colors.onDarkFaint}>{t('landing.eyebrow')}</Eyebrow>

        <Typography sx={{
          ...type.display,
          fontSize: { xs: '38px', sm: '54px', md: '68px' },
          color: colors.onDark,
          mt: 2,
        }}>
          {t('landing.title')}
        </Typography>
        <Typography sx={{
          ...type.display,
          fontWeight: 300,
          fontSize: { xs: '38px', sm: '54px', md: '68px' },
          color: colors.blueSoft,
          mb: 4,
        }}>
          {t('landing.subtitle')}
        </Typography>

        <Typography sx={{
          ...type.body,
          fontSize: { xs: '16px', md: '18px' },
          color: colors.onDarkMuted,
          maxWidth: '640px',
        }}>
          {t('landing.intro')}
        </Typography>
        </Box>
      </Box>

      {/* Prototype grid */}
      <Box sx={{
        flex: 1,
        backgroundColor: colors.blueWash,
        borderTop: `1px solid ${colors.border}`,
        py: { xs: 6, md: 9 },
      }}>
        <Box sx={{ maxWidth: layout.maxWidth, mx: 'auto', px: layout.gutter }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 4 }}>
            <Eyebrow>{t('landing.demonstrators')}</Eyebrow>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: colors.border }} />
            <Typography sx={{ ...type.meta, color: colors.inkGhost }}>
              {String(PROTOTYPES.filter(p => p.status === 'available').length).padStart(2, '0')}
            </Typography>
          </Box>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 2.5, md: 3 },
          }}>
            {PROTOTYPES.map((proto) => {
              const open = proto.status === 'available';
              return (
                <Box
                  key={proto.num}
                  onClick={() => open && navigate(proto.path)}
                  role={open ? 'button' : undefined}
                  tabIndex={open ? 0 : undefined}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (open && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      navigate(proto.path);
                    }
                  }}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: layout.radiusLg,
                    p: { xs: 3, md: 4 },
                    pt: { xs: 3.5, md: 4.5 },
                    cursor: open ? 'pointer' : 'default',
                    boxShadow: elevation.card,
                    transition: 'box-shadow .18s ease, border-color .18s ease, transform .18s ease',
                    outline: 'none',
                    overflow: 'hidden',
                    opacity: open ? 1 : 0.6,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: 4,
                      width: open ? 64 : 32,
                      backgroundColor: open ? colors.accent : colors.borderStrong,
                      transition: 'width .22s ease',
                    },
                    ...(open && {
                      '&:hover, &:focus-visible': {
                        borderColor: colors.borderStrong,
                        boxShadow: elevation.cardHover,
                        transform: 'translateY(-2px)',
                        backgroundColor: colors.surfaceRaised,
                      },
                      '&:hover::before, &:focus-visible::before': { width: '100%' },
                      '&:hover .proto-cta, &:focus-visible .proto-cta': { color: colors.ink },
                      '&:hover .proto-arrow, &:focus-visible .proto-arrow': { transform: 'translateX(4px)' },
                    }),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                    <Eyebrow color={open ? colors.inkFaint : colors.inkGhost}>
                      {t(`proto.${proto.num}.tag`)}
                    </Eyebrow>
                    <Typography sx={{
                      fontFamily: font.mono,
                      fontSize: '13px',
                      fontWeight: 500,
                      color: colors.inkGhost,
                      lineHeight: 1,
                    }}>
                      {proto.num}
                    </Typography>
                  </Box>

                  <Typography sx={{
                    ...type.heading,
                    fontSize: { xs: '20px', md: '23px' },
                    color: colors.ink,
                    mb: 1.5,
                  }}>
                    {t(`proto.${proto.num}.title`)}
                  </Typography>

                  <Typography sx={{
                    ...type.body,
                    fontSize: '14px',
                    color: colors.inkMuted,
                    mb: 4,
                    flex: 1,
                  }}>
                    {t(`proto.${proto.num}.desc`)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      className="proto-cta"
                      sx={{
                        ...type.eyebrow,
                        fontSize: '12px',
                        color: open ? colors.inkMuted : colors.inkGhost,
                        transition: 'color .18s ease',
                      }}
                    >
                      {open ? t('landing.open') : t('landing.comingSoon')}
                    </Typography>
                    {open && (
                      <Box
                        className="proto-arrow"
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          transition: 'transform .18s ease',
                        }}
                      >
                        {/* Styleguide 'green bubble' */}
                        <Bubble size={30}>→</Bubble>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{
        borderTop: `1px solid ${colors.border}`,
        backgroundColor: colors.bg,
        py: 3,
      }}>
        <Box sx={{
          maxWidth: layout.maxWidth,
          mx: 'auto',
          px: layout.gutter,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}>
          <Typography sx={{ ...type.meta, color: colors.inkGhost, letterSpacing: '0.12em' }}>
            {t('landing.env')}
          </Typography>
          <Typography sx={{ ...type.meta, color: colors.inkGhost }}>
            {t('landing.footerNote')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;
